import {
  Zap,
  TrendingUp,
  TrendingDown,
  Minus,
  Phone,
  Mail,
  MessageSquare,
  Clock,
  Sparkles,
  Building2,
  ArrowUpRight,
  ShieldOff,
  Target,
  Radio,
  Calendar,
  Award,
} from "lucide-react";
import { useDemo } from "@/demo/DemoContext";

/* --- Data --------------------------------------------------------- */

const LEADS = [
  // 3 stars (CVS 85+)
  { id: "cve-1", name: "Dr. Marcus Webb", company: "Regional Surgical Group", cvs: 91, trend: "up", signal: "Replied to SMS 47 min ago. Clicked booking link. Awaiting confirmation.", channel: "SMS", days: 4, star: true },
  { id: "cve-2", name: "Dr. Priya Nair", company: "Pacific Surgical", cvs: 87, trend: "up", signal: "Live call in progress — qualifying.", channel: "Voice", days: 2, star: true, live: true },
  { id: "cve-3", name: "Amanda Torres", company: "Valley Medical", cvs: 85, trend: "up", signal: "Opened 3 emails, clicked case study link.", channel: "Email", days: 6, star: true },
  // Teal (60–84)
  { id: "cve-4",  name: "Dr. Sarah Chen",   company: "Midwest Health",     cvs: 78, trend: "up",   signal: "Opened Day 2 SMS 12 min ago.",              channel: "SMS",   days: 3 },
  { id: "cve-5",  name: "Dr. Lisa Tran",    company: "Summit Orthopedics", cvs: 74, trend: "up",   signal: "Booked demo — awaiting rep call.",         channel: "Voice", days: 1 },
  { id: "cve-6",  name: "James Park",       company: "NovaMed Instruments",cvs: 70, trend: "flat", signal: "Confirmed demo · pre-call reminder sent.", channel: "Email", days: 2 },
  { id: "cve-7",  name: "Dr. Anita Rao",    company: "Coastal Cardiology", cvs: 66, trend: "up",   signal: "Clicked case study link 2h ago.",           channel: "Email", days: 5 },
  { id: "cve-8",  name: "Michael Sato",     company: "Alpine Surgical",    cvs: 63, trend: "up",   signal: "Replied to Day 3 email — asked about ROI.", channel: "Email", days: 7 },
  { id: "cve-9",  name: "Dr. Elena Ruiz",   company: "Bay Area Ortho",     cvs: 61, trend: "flat", signal: "Opened 4/5 emails · no clicks yet.",         channel: "Email", days: 9 },
  // Amber (30–59)
  { id: "cve-10", name: "Priya Desai",      company: "Prairie Health",     cvs: 55, trend: "flat", signal: "SMS delivered · not opened.",                channel: "SMS",   days: 4 },
  { id: "cve-11", name: "Dr. Kenji Wu",     company: "Sunbelt Devices",    cvs: 48, trend: "down", signal: "Voicemail left · no callback (2 attempts).", channel: "Voice", days: 8 },
  { id: "cve-12", name: "Rachel Green",     company: "Lakeside Medical",   cvs: 44, trend: "flat", signal: "Opened Day 1 email · silent since.",          channel: "Email", days: 6 },
  { id: "cve-13", name: "Dr. Omar Haddad",  company: "Northern Peaks",     cvs: 40, trend: "down", signal: "No engagement after 4 touches.",              channel: "Email", days: 10 },
  { id: "cve-14", name: "Chris Wells",      company: "Cascade Surgical",   cvs: 38, trend: "flat", signal: "Email bounced once · re-verified.",           channel: "Email", days: 7 },
  { id: "cve-15", name: "Dr. Naomi Park",   company: "Delta Medical",      cvs: 34, trend: "down", signal: "Opted-out of SMS · email only.",             channel: "Email", days: 9 },
  { id: "cve-16", name: "Alex Petrov",      company: "Empire Devices",     cvs: 31, trend: "down", signal: "Missed 2 voice attempts.",                    channel: "Voice", days: 12 },
  { id: "cve-17", name: "Dr. Ivy Chen",     company: "Meridian Ortho",     cvs: 30, trend: "flat", signal: "Auto-scheduled to Day 8 re-touch.",           channel: "Email", days: 8 },
  // Red (<30)
  { id: "cve-18", name: "Robert Kim",   company: "Great Lakes Devices", cvs: 22, trend: "down", signal: "No engagement after 6 touches. Final email queued.", channel: "Email", days: 11, danger: true },
  { id: "cve-19", name: "Kevin Marsh",  company: "Heartland Hospital",  cvs: 18, trend: "down", signal: "No answer, voicemail full. Disqualification pending.", channel: "Voice", days: 8,  danger: true },
  { id: "cve-20", name: "Jared Wolfe",  company: "Redwood Health",      cvs: 12, trend: "down", signal: "Bounced 3× · flagged for archive.",                   channel: "Email", days: 14, danger: true },
];

const CVS_FACTORS = [
  {
    id: "f-voice",
    label: "Voice Engagement",
    max: 35,
    Icon: Phone,
    accent: "#00C2A8",
    rows: [
      { k: "Connected call", v: "+25" },
      { k: "Voicemail callback", v: "+35" },
      { k: "No answer", v: "0" },
      { k: "Disengaged", v: "−10", danger: true },
    ],
  },
  {
    id: "f-email",
    label: "Email Engagement",
    max: 25,
    Icon: Mail,
    accent: "#F59E0B",
    rows: [
      { k: "Open", v: "+8" },
      { k: "Click", v: "+15" },
      { k: "Reply", v: "+25" },
    ],
  },
  {
    id: "f-sms",
    label: "SMS Engagement",
    max: 25,
    Icon: MessageSquare,
    accent: "#0EA5E9",
    rows: [
      { k: "Open", v: "+8" },
      { k: "Click", v: "+15" },
      { k: "Reply", v: "+25" },
    ],
  },
  {
    id: "f-time",
    label: "Time Decay",
    max: 15,
    Icon: Clock,
    accent: "#EF4444",
    rows: [
      { k: "< 3 days in system", v: "Full" },
      { k: "3 – 7 days", v: "−5" },
      { k: "7 – 14 days", v: "−10" },
      { k: "14+ days", v: "−15", danger: true },
    ],
  },
];

/* --- Helpers ------------------------------------------------------ */

function cvsColor(v) {
  if (v >= 85) return "#00C2A8";
  if (v >= 60) return "#0EA5E9";
  if (v >= 30) return "#F59E0B";
  return "#EF4444";
}
function channelIcon(ch) {
  if (ch === "Voice") return Phone;
  if (ch === "SMS") return MessageSquare;
  return Mail;
}
function TrendIcon({ trend, color }) {
  if (trend === "up") return <TrendingUp size={13} color={color} />;
  if (trend === "down") return <TrendingDown size={13} color={color} />;
  return <Minus size={13} color={color} />;
}

/* --- Circular ring ------------------------------------------------ */

function CVSRing({ value, size = 68, stroke = 5, star, danger }) {
  const R = (size - stroke) / 2;
  const C = 2 * Math.PI * R;
  const offset = C - (value / 100) * C;
  const color = cvsColor(value);
  const glow = star ? `drop-shadow(0 0 8px ${color})` : undefined;

  return (
    <div style={{ position: "relative", width: size, height: size, flexShrink: 0 }}>
      {star && (
        <span
          aria-hidden
          style={{
            position: "absolute",
            inset: -3,
            borderRadius: 999,
            border: `1.5px solid ${color}55`,
            animation: "pulse-dot 1.8s ease-out infinite",
          }}
        />
      )}
      <svg width={size} height={size} style={{ transform: "rotate(-90deg)", filter: glow }}>
        <circle
          cx={size / 2} cy={size / 2} r={R}
          fill="none"
          stroke="rgba(30, 48, 72, 0.6)"
          strokeWidth={stroke}
        />
        <circle
          cx={size / 2} cy={size / 2} r={R}
          fill="none"
          stroke={color}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={C}
          strokeDashoffset={offset}
          style={{ transition: "stroke-dashoffset 800ms ease" }}
        />
      </svg>
      <div
        style={{
          position: "absolute",
          inset: 0,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexDirection: "column",
          gap: 0,
        }}
      >
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontWeight: 700,
            fontSize: 18,
            color: color,
            letterSpacing: "-0.02em",
            lineHeight: 1,
          }}
        >
          {value}
        </div>
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 7.5,
            letterSpacing: "0.12em",
            color: danger ? "#EF4444" : "var(--text-muted)",
            textTransform: "uppercase",
            marginTop: 2,
          }}
        >
          CVS
        </div>
      </div>
    </div>
  );
}

/* --- Lead card ---------------------------------------------------- */

function LeadCard({ lead, i }) {
  const color = cvsColor(lead.cvs);
  const ChIcon = channelIcon(lead.channel);

  return (
    <article
      data-testid={lead.id}
      className="card-glass card-interactive enter-up relative overflow-hidden"
      style={{
        animationDelay: `${i * 40}ms`,
        padding: 14,
        borderColor: lead.star
          ? `${color}45`
          : lead.danger
          ? "rgba(239, 68, 68, 0.30)"
          : "var(--border-divider)",
        background: lead.star
          ? `linear-gradient(135deg, ${color}10 0%, transparent 100%), var(--bg-elevated)`
          : lead.danger
          ? "linear-gradient(135deg, rgba(239, 68, 68, 0.05) 0%, transparent 100%), var(--bg-elevated)"
          : "var(--bg-elevated)",
        boxShadow: lead.star ? `0 0 22px ${color}22` : undefined,
      }}
    >
      {/* Star / Danger tag */}
      {lead.star && (
        <span
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 8.5,
            letterSpacing: "0.14em",
            color: color,
            padding: "2px 5px",
            borderRadius: 3,
            background: `${color}18`,
            border: `1px solid ${color}45`,
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          <Award size={9} />
          HOT
        </span>
      )}
      {lead.danger && (
        <span
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 8.5,
            letterSpacing: "0.14em",
            color: "#EF4444",
            padding: "2px 5px",
            borderRadius: 3,
            background: "rgba(239, 68, 68, 0.10)",
            border: "1px solid rgba(239, 68, 68, 0.35)",
            display: "inline-flex",
            alignItems: "center",
            gap: 3,
          }}
        >
          <ShieldOff size={9} />
          AT RISK
        </span>
      )}

      <div className="flex items-start gap-3">
        <CVSRing value={lead.cvs} star={lead.star} danger={lead.danger} />

        <div className="flex-1 min-w-0">
          <div
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: "-0.01em",
              color: "var(--text-primary)",
              lineHeight: 1.2,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              paddingRight: 40,
            }}
          >
            {lead.name}
          </div>
          <div
            className="flex items-center gap-1 mt-1"
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 11,
              color: "var(--text-secondary)",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            <Building2 size={10} color="var(--text-muted)" />
            <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{lead.company}</span>
          </div>

          <div
            className="flex items-center gap-2 mt-2"
            style={{
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10,
              color: "var(--text-muted)",
            }}
          >
            <span
              className="inline-flex items-center gap-1"
              style={{ color: color }}
            >
              <TrendIcon trend={lead.trend} color={color} />
              {lead.trend === "up" ? "RISING" : lead.trend === "down" ? "DECLINING" : "FLAT"}
            </span>
            <span style={{ color: "var(--border-hover)" }}>·</span>
            <span>{lead.days}d</span>
          </div>
        </div>
      </div>

      {/* Signal */}
      <div
        className="mt-3 pt-3 flex items-start gap-2"
        style={{ borderTop: "1px dashed rgba(30, 48, 72, 0.7)" }}
      >
        <div
          className="flex items-center justify-center rounded"
          style={{
            width: 20, height: 20,
            background: `${color}12`,
            border: `1px solid ${color}30`,
            flexShrink: 0,
            marginTop: 1,
            position: "relative",
          }}
        >
          <ChIcon size={10} color={color} />
          {lead.live && (
            <span
              style={{
                position: "absolute",
                top: -2, right: -2,
                width: 6, height: 6, borderRadius: 999,
                background: "#F59E0B",
                boxShadow: "0 0 8px rgba(245, 158, 11, 0.7)",
                animation: "pulse-dot 1.4s ease-out infinite",
              }}
            />
          )}
        </div>
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 11,
            lineHeight: 1.45,
            color: "var(--text-primary)",
            margin: 0,
            flex: 1,
          }}
        >
          {lead.signal}
        </p>
      </div>
    </article>
  );
}

/* --- Sections ----------------------------------------------------- */

function Hero() {
  return (
    <section
      data-testid="cve-hero"
      className="card-glass p-6 relative overflow-hidden"
    >
      {/* Subtle grid backdrop */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          inset: 0,
          backgroundImage:
            "linear-gradient(rgba(0, 194, 168, 0.05) 1px, transparent 1px), linear-gradient(90deg, rgba(0, 194, 168, 0.05) 1px, transparent 1px)",
          backgroundSize: "40px 40px",
          backgroundPosition: "-1px -1px",
          maskImage: "radial-gradient(ellipse 800px 500px at 30% 0%, black 30%, transparent 100%)",
          WebkitMaskImage: "radial-gradient(ellipse 800px 500px at 30% 0%, black 30%, transparent 100%)",
          pointerEvents: "none",
        }}
      />

      <div className="relative">
        <header className="flex items-center justify-between mb-5">
          <div className="flex items-center gap-2.5">
            <div
              className="flex items-center justify-center rounded-md"
              style={{ width: 30, height: 30, background: "rgba(0, 194, 168, 0.14)", border: "1px solid rgba(0, 194, 168, 0.40)" }}
            >
              <Zap size={15} color="#00C2A8" />
            </div>
            <div>
              <h2 style={{ fontFamily: "Inter, sans-serif", fontWeight: 700, fontSize: 16, color: "var(--text-primary)", letterSpacing: "-0.01em" }}>
                Live Lead Velocity Grid
              </h2>
              <div style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10.5, letterSpacing: "0.12em", color: "var(--text-muted)", textTransform: "uppercase" }}>
                20 leads · CVS updated per interaction · streaming
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden md:flex items-center gap-4" style={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 10, color: "var(--text-muted)", letterSpacing: "0.06em" }}>
              {[
                { c: "#00C2A8", t: "85+ HOT" },
                { c: "#0EA5E9", t: "60–84" },
                { c: "#F59E0B", t: "30–59" },
                { c: "#EF4444", t: "< 30" },
              ].map((l, i) => (
                <span key={i} className="inline-flex items-center gap-1.5">
                  <span style={{ width: 8, height: 8, borderRadius: 999, background: l.c, boxShadow: `0 0 6px ${l.c}66` }} />
                  {l.t}
                </span>
              ))}
            </div>
            <span className="pill pill-teal" style={{ fontSize: 10 }}>
              <span className="pill-dot pill-dot-pulse" />
              Live
            </span>
          </div>
        </header>

        <div
          className="grid gap-3"
          style={{
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          }}
        >
          {LEADS.map((l, i) => <LeadCard key={l.id} lead={l} i={i} />)}
        </div>
      </div>
    </section>
  );
}

function FactorCard({ f, i }) {
  const { Icon } = f;
  return (
    <article
      data-testid={f.id}
      className="card-glass p-5 enter-up"
      style={{
        animationDelay: `${i * 80}ms`,
        borderTop: `3px solid ${f.accent}`,
        background: `linear-gradient(180deg, ${f.accent}0A 0%, transparent 30%), var(--bg-elevated)`,
      }}
    >
      <div className="flex items-center justify-between mb-3">
        <div
          className="flex items-center justify-center rounded-lg"
          style={{ width: 34, height: 34, background: `${f.accent}14`, border: `1px solid ${f.accent}30` }}
        >
          <Icon size={16} color={f.accent} />
        </div>
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10.5,
            letterSpacing: "0.10em",
            color: f.accent,
            padding: "3px 8px",
            borderRadius: 4,
            background: `${f.accent}12`,
            border: `1px solid ${f.accent}30`,
            textTransform: "uppercase",
          }}
        >
          Max {f.max} pts
        </div>
      </div>

      <h3
        style={{
          fontFamily: "Inter, sans-serif",
          fontWeight: 700,
          fontSize: 14,
          letterSpacing: "-0.01em",
          color: "var(--text-primary)",
          marginBottom: 12,
        }}
      >
        {f.label}
      </h3>

      <ul className="space-y-2">
        {f.rows.map((r, ri) => (
          <li key={ri} className="flex items-center justify-between">
            <span style={{ fontFamily: "Inter, sans-serif", fontSize: 12, color: "var(--text-secondary)" }}>
              {r.k}
            </span>
            <span
              style={{
                fontFamily: "'JetBrains Mono', monospace",
                fontWeight: 600,
                fontSize: 12.5,
                color: r.danger ? "#EF4444" : r.v.startsWith("+") ? "#00C2A8" : "var(--text-primary)",
              }}
            >
              {r.v}
            </span>
          </li>
        ))}
      </ul>
    </article>
  );
}

function HowCalculated() {
  return (
    <section data-testid="cvs-formula" className="space-y-5">
      <div className="flex items-center gap-3">
        <div
          className="flex items-center justify-center rounded-md"
          style={{ width: 26, height: 26, background: "rgba(0, 194, 168, 0.10)", border: "1px solid rgba(0, 194, 168, 0.30)" }}
        >
          <Sparkles size={12} color="#00C2A8" />
        </div>
        <div
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10.5,
            letterSpacing: "0.14em",
            color: "var(--text-muted)",
            textTransform: "uppercase",
          }}
        >
          How CVS Is Calculated
        </div>
        <div style={{ flex: 1, height: 1, background: "var(--border-divider)" }} />
        <span
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10,
            color: "#00C2A8",
            letterSpacing: "0.06em",
          }}
        >
          4 FACTORS · 100 PT SCALE
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-4">
        {CVS_FACTORS.map((f, i) => <FactorCard key={f.id} f={f} i={i} />)}
      </div>

      <div
        className="rounded-lg p-4 flex items-start gap-3"
        style={{
          background: "rgba(15, 25, 35, 0.5)",
          border: "1px dashed rgba(0, 194, 168, 0.30)",
        }}
      >
        <Radio size={14} color="#00C2A8" style={{ marginTop: 2, flexShrink: 0 }} />
        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 13,
            lineHeight: 1.55,
            color: "var(--text-primary)",
            margin: 0,
          }}
        >
          <span style={{ color: "#00C2A8", fontWeight: 600 }}>CVS updates within seconds</span> of every agent interaction. No manual input. No CRM entry required.
        </p>
      </div>
    </section>
  );
}

function WhyItMatters() {
  const panels = [
    {
      id: "wm-without",
      label: "Without CVS",
      accent: "#EF4444",
      Icon: ShieldOff,
      body:
        "Your pipeline shows 487 leads. You don't know which 31 are 48 hours from a yes and which 300 are already gone. Reps guess. Leads go cold. Acquisition cost: wasted.",
    },
    {
      id: "wm-with",
      label: "With CVS",
      accent: "#00C2A8",
      Icon: Target,
      body:
        "Every rep knows exactly which leads need a personal call right now. Every agent knows which sequence to accelerate. The $162–$286 acquisition cost behind each lead is protected by data, not guesswork.",
      featured: true,
    },
  ];

  return (
    <section data-testid="why-matters" className="grid grid-cols-1 md:grid-cols-2 gap-4 relative">
      {panels.map((p, i) => {
        const { Icon } = p;
        return (
          <article
            key={p.id}
            data-testid={p.id}
            className="card-glass p-6 enter-up relative overflow-hidden"
            style={{
              animationDelay: `${i * 100}ms`,
              borderLeft: `3px solid ${p.accent}`,
              background: p.featured
                ? `linear-gradient(135deg, ${p.accent}12 0%, transparent 100%), var(--bg-elevated)`
                : "var(--bg-elevated)",
              boxShadow: p.featured ? `0 0 24px ${p.accent}20` : undefined,
            }}
          >
            <div className="flex items-center gap-2.5 mb-4">
              <div
                className="flex items-center justify-center rounded-md"
                style={{ width: 30, height: 30, background: `${p.accent}14`, border: `1px solid ${p.accent}30` }}
              >
                <Icon size={14} color={p.accent} />
              </div>
              <div
                style={{
                  fontFamily: "Inter, sans-serif",
                  fontWeight: 700,
                  fontSize: 18,
                  letterSpacing: "-0.02em",
                  color: p.accent,
                }}
              >
                {p.label}
              </div>
            </div>
            <p
              style={{
                fontFamily: "Inter, sans-serif",
                fontSize: 14,
                lineHeight: 1.65,
                color: "var(--text-primary)",
                margin: 0,
              }}
            >
              {p.body}
            </p>
          </article>
        );
      })}
    </section>
  );
}

function FinalCTA() {
  const { openBooking } = useDemo();
  return (
    <section
      data-testid="final-cta"
      className="card-glass relative overflow-hidden text-center enter-up"
      style={{
        padding: "56px 32px",
        background:
          "radial-gradient(ellipse 900px 420px at 50% 0%, rgba(0, 194, 168, 0.14) 0%, transparent 60%), linear-gradient(180deg, rgba(0, 194, 168, 0.03), transparent), var(--bg-elevated)",
        border: "1px solid rgba(0, 194, 168, 0.30)",
      }}
    >
      {/* Ambient orbs */}
      <div
        aria-hidden
        style={{
          position: "absolute",
          top: -60, left: "50%",
          transform: "translateX(-50%)",
          width: 480, height: 240,
          background: "radial-gradient(ellipse, rgba(14, 165, 233, 0.20) 0%, transparent 70%)",
          filter: "blur(30px)",
          pointerEvents: "none",
        }}
      />

      <div className="relative max-w-[720px] mx-auto">
        <div
          className="inline-flex items-center gap-2 rounded-full mb-6"
          style={{
            padding: "5px 12px",
            background: "rgba(0, 194, 168, 0.10)",
            border: "1px solid rgba(0, 194, 168, 0.35)",
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10.5,
            letterSpacing: "0.14em",
            color: "#00C2A8",
            textTransform: "uppercase",
          }}
        >
          <Sparkles size={11} />
          Ready to run this on your pipeline?
        </div>

        <h2
          style={{
            fontFamily: "Inter, sans-serif",
            fontWeight: 800,
            fontSize: 34,
            letterSpacing: "-0.03em",
            color: "var(--text-primary)",
            lineHeight: 1.15,
            marginBottom: 16,
          }}
        >
          Stop guessing which MQL is{" "}
          <span style={{ color: "#00C2A8" }}>hot</span>. Start knowing.
        </h2>

        <p
          style={{
            fontFamily: "Inter, sans-serif",
            fontSize: 15,
            lineHeight: 1.6,
            color: "var(--text-secondary)",
            maxWidth: 620,
            margin: "0 auto 32px",
          }}
        >
          We'll map your current MQL-to-SQL gap and show you what Tier 2 looks like{" "}
          <span style={{ color: "var(--text-primary)", fontWeight: 500 }}>
            built on your actual lead data.
          </span>
        </p>

        <button
          data-testid="final-audit-cta"
          onClick={openBooking}
          className="inline-flex items-center gap-2.5 rounded-xl"
          style={{
            padding: "16px 28px",
            background: "linear-gradient(135deg, #00C2A8 0%, #0EA5E9 100%)",
            color: "#0F1923",
            fontFamily: "Inter, sans-serif",
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: "-0.005em",
            border: "none",
            cursor: "pointer",
            transition: "transform 200ms ease, box-shadow 200ms ease",
            boxShadow: "0 0 40px rgba(0, 194, 168, 0.45), 0 8px 24px rgba(0, 0, 0, 0.4)",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "translateY(-2px)";
            e.currentTarget.style.boxShadow = "0 0 60px rgba(0, 194, 168, 0.65), 0 12px 32px rgba(0, 0, 0, 0.5)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "translateY(0)";
            e.currentTarget.style.boxShadow = "0 0 40px rgba(0, 194, 168, 0.45), 0 8px 24px rgba(0, 0, 0, 0.4)";
          }}
        >
          <Calendar size={17} strokeWidth={2.4} />
          Book Your Growth Audit — 30 Min
          <ArrowUpRight size={17} strokeWidth={2.4} />
        </button>

        <div
          className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2"
          style={{
            fontFamily: "'JetBrains Mono', monospace",
            fontSize: 10.5,
            letterSpacing: "0.06em",
            color: "var(--text-muted)",
            textTransform: "uppercase",
          }}
        >
          <span className="inline-flex items-center gap-1.5">
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#22C55E" }} />
            No sales pitch
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#00C2A8" }} />
            Your data · your gap analysis
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span style={{ width: 6, height: 6, borderRadius: 999, background: "#0EA5E9" }} />
            30 minutes · calendar link inbound
          </span>
        </div>
      </div>
    </section>
  );
}

/* --- Page --------------------------------------------------------- */

export default function VelocityEngine() {
  return (
    <div data-testid="velocity-engine-page" className="max-w-[1400px] mx-auto space-y-10">
      {/* Centered hero header */}
      <section data-testid="cve-header" className="text-center enter-up pt-4 relative">
        <div
          aria-hidden
          style={{
            position: "absolute",
            top: -40,
            left: "50%",
            transform: "translateX(-50%)",
            width: 720,
            height: 260,
            background:
              "radial-gradient(ellipse, rgba(0, 194, 168, 0.14) 0%, transparent 70%)",
            filter: "blur(24px)",
            pointerEvents: "none",
            zIndex: 0,
          }}
        />
        <div className="relative" style={{ zIndex: 1 }}>
          <div
            className="inline-flex items-center gap-2 rounded-full mb-5"
            style={{
              padding: "5px 12px",
              background: "linear-gradient(135deg, rgba(0, 194, 168, 0.12), rgba(14, 165, 233, 0.10))",
              border: "1px solid rgba(0, 194, 168, 0.35)",
              fontFamily: "'JetBrains Mono', monospace",
              fontSize: 10.5,
              letterSpacing: "0.16em",
              color: "#00C2A8",
              textTransform: "uppercase",
            }}
          >
            <Zap size={11} strokeWidth={2.4} />
            Flagship Capability
          </div>

          <h1
            style={{
              fontFamily: "Inter, sans-serif",
              fontWeight: 800,
              fontSize: 44,
              letterSpacing: "-0.03em",
              lineHeight: 1.05,
              color: "var(--text-primary)",
              marginBottom: 16,
              maxWidth: 900,
              marginInline: "auto",
            }}
            data-testid="cve-title"
          >
            Conversion{" "}
            <span
              style={{
                background: "linear-gradient(135deg, #00C2A8 0%, #0EA5E9 100%)",
                WebkitBackgroundClip: "text",
                backgroundClip: "text",
                color: "transparent",
              }}
            >
              Velocity
            </span>{" "}
            Engine
          </h1>

          <p
            style={{
              fontFamily: "Inter, sans-serif",
              fontSize: 16,
              lineHeight: 1.55,
              color: "var(--text-secondary)",
              maxWidth: 820,
              marginInline: "auto",
            }}
          >
            The only view in medical device sales that tells you,{" "}
            <span style={{ color: "var(--text-primary)", fontWeight: 600 }}>right now</span>, which of your{" "}
            <span style={{ color: "#0EA5E9", fontWeight: 600 }}>487 MQLs</span> is{" "}
            <span style={{ color: "#00C2A8", fontWeight: 600 }}>4 hours from booking a demo</span> — and which one you're about to{" "}
            <span style={{ color: "#EF4444", fontWeight: 600 }}>lose forever</span>.
          </p>
        </div>
      </section>

      {/* Hero grid */}
      <Hero />

      {/* How CVS is calculated */}
      <HowCalculated />

      {/* Why it matters */}
      <WhyItMatters />

      {/* Final CTA */}
      <FinalCTA />
    </div>
  );
}
